import { FIDES_SETUP_DEFAULTS } from '../../data/seed/seedSetup';
import type { FundOpportunity, SetupSource, SetupValue, StaffUser } from '../../data/types';
import { addMonths } from '../../lib/format';

type Values = Record<string, SetupValue>;

export const LOGIN_DOMAIN = 'invest.fidescapital.com.au';

const text = (values: Values, key: string) => String(values[key] ?? '');

export const DERIVED: Record<string, (values: Values) => SetupValue> = {
  'brand.loginUrl': (values) => `${LOGIN_DOMAIN}/${text(values, 'fund.code').toLowerCase()}`,
  'bank.accountName': (values) => `${text(values, 'fund.name')} Applications Account`,
  'cert.companyCode': (values) => text(values, 'fund.code'),
  'cert.securityCode': (values) => text(values, 'class.code'),
  'price.entry': (values) => text(values, 'class.issuePrice'),
  'price.mid': (values) => text(values, 'class.issuePrice'),
  'price.exit': (values) => text(values, 'class.issuePrice'),
  'price.date': (values) => text(values, 'fund.inceptionDate'),
  'offer.payableNow': (values) => {
    const price = Number(text(values, 'class.issuePrice')) || 1;
    const share =
      values['class.partlyPaid'] === true
        ? (Number(text(values, 'class.paidOnApplication')) || 100) / 100
        : 1;
    return (price * share).toFixed(2);
  },
  'offer.payDate': (values) => text(values, 'offer.closeDate'),
  'offer.title': (values) => `${text(values, 'fund.name')} – ${text(values, 'class.name')}`,
  'offer.url': (values) => `${text(values, 'brand.loginUrl')}/offer`,
  'offer.summary': (values) => text(values, 'fund.summary'),
  'inv.alertEmail': (values) => text(values, 'account.email'),
  'comms.replyTo': (values) => text(values, 'contact.email'),
  'notify.newApplication': (values) => text(values, 'account.email'),
};

export function fundCodeFor(name: string, takenCodes: string[]): string {
  const words = name.replace(/\(.*?\)/g, ' ').match(/[A-Za-z0-9]+/g) ?? [];
  const base =
    words.map((word) => (/^\d+$/.test(word) ? word : word[0].toUpperCase())).join('') || 'FUND';
  const taken = new Set(takenCodes.map((code) => code.toUpperCase()));
  let code = base;
  for (let suffix = 2; taken.has(code); suffix += 1) code = `${base}${suffix}`;
  return code;
}

export function accountContactFor(user: StaffUser): Values {
  return {
    'account.userId': user.id,
    'account.name': user.name,
    'account.role': user.title ?? '',
    'account.email': user.email ?? '',
    'account.phone': user.phone ?? '',
    'account.address': user.address ?? '',
  };
}

export function buildPrefill(
  opportunity: FundOpportunity,
  user: StaffUser,
  houseDefaults: Record<string, SetupValue> = FIDES_SETUP_DEFAULTS,
  takenCodes: string[] = [],
) {
  const values: Values = {};
  const sources: Record<string, SetupSource> = {};
  const put = (source: SetupSource, entries: Values) => {
    for (const [key, value] of Object.entries(entries)) {
      if (value === '' || value === undefined) continue;
      values[key] = value;
      sources[key] = source;
    }
  };
  put('FIDES', houseDefaults);
  put('PROFILE', accountContactFor(user));
  put('OPPORTUNITY', {
    'fund.name': opportunity.name,
    'fund.targetSize': String(opportunity.targetSize),
    'fund.maturityDate': opportunity.termMonths
      ? addMonths(opportunity.eoiCloseDate, opportunity.termMonths)
      : '',
    'class.targetReturn': String(opportunity.targetReturnPct),
    'offer.closeDate': opportunity.eoiCloseDate,
  });
  values['fund.code'] = fundCodeFor(opportunity.name, takenCodes);
  sources['fund.code'] = 'DERIVED';
  for (const [key, derive] of Object.entries(DERIVED)) {
    values[key] = derive(values);
    sources[key] = 'DERIVED';
  }
  return { values, sources };
}

export function applyPatch(values: Values, prefill: Values, patch: Values) {
  const nextValues = { ...values, ...patch };
  const nextPrefill = { ...prefill };
  for (const [key, derive] of Object.entries(DERIVED)) {
    const unedited = values[key] === prefill[key] && !(key in patch);
    const derived = derive(nextValues);
    nextPrefill[key] = derived;
    if (unedited) nextValues[key] = derived;
  }
  return { values: nextValues, prefill: nextPrefill };
}
